import debounce from 'lodash/debounce';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {getWindow} from 'ssr-window';
import EnvironmentUtils from '@spothero/utils/environment';
import WalkingDistanceDisplay from './WalkingDistanceDisplay';

const window = getWindow();

export default class SpotDetailsDirectionsMap extends Component {
    static propTypes = {
        spotLatitude: PropTypes.number.isRequired,
        spotLongitude: PropTypes.number.isRequired,
        destinationLatitude: PropTypes.number.isRequired,
        destinationLongitude: PropTypes.number.isRequired,
        isDriving: PropTypes.bool,
        isDraggable: PropTypes.bool,
        disableDefaultUI: PropTypes.bool,
        distance: PropTypes.number,
        distanceInMiles: PropTypes.number,
        walkingDuration: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
        showDuration: PropTypes.bool,
        onClick: PropTypes.func,
    };

    constructor(props) {
        super(props);

        const isBrowser = EnvironmentUtils.isBrowser();
        const {
            spotLatitude,
            spotLongitude,
            destinationLatitude,
            destinationLongitude,
            isDriving,
        } = props;

        this._iconSize = 32;
        this._gmaps = isBrowser ? window.google.maps : null;
        this._iconBasePath = `https://res.cloudinary.com/spothero/image/upload/c_fill,dpr_2.0,w_${this._iconSize}/v1579014835/google-assistant`;
        this._directionsService = isBrowser
            ? new this._gmaps.DirectionsService()
            : null;
        this._directionsDisplay = isBrowser
            ? new this._gmaps.DirectionsRenderer({
                  polylineOptions: {
                      icons: isDriving
                          ? null
                          : [
                                {
                                    icon: {
                                        path: this._gmaps.SymbolPath.CIRCLE,
                                        fillOpacity: 1,
                                        scale: 3,
                                    },
                                    repeat: '12px',
                                },
                            ],
                      strokeColor: '#0082ff',
                      strokeOpacity: isDriving ? 1 : 0,
                      strokeWeight: 2,
                  },
                  suppressMarkers: true,
              })
            : null;
        this._spotLatLng = isBrowser
            ? new this._gmaps.LatLng(spotLatitude, spotLongitude)
            : null;
        this._destinationLatLng = isBrowser
            ? new this._gmaps.LatLng(destinationLatitude, destinationLongitude)
            : null;
        this._bounds = isBrowser ? new this._gmaps.LatLngBounds() : null;

        if (isBrowser) {
            this._bounds.extend(this._spotLatLng);
            this._bounds.extend(this._destinationLatLng);
        }
    }

    componentDidMount() {
        const {isDraggable, disableDefaultUI, onClick} = this.props;

        setTimeout(() => {
            const mapOptions = {
                zoom: 17,
                center: this._destinationLatLng,
                mapTypeId: this._gmaps.MapTypeId.ROADMAP,
                gestureHandling: isDraggable ? 'auto' : 'none',
                keyboardShortcuts: isDraggable,
                clickableIcons: false,
            };

            if (disableDefaultUI) {
                mapOptions.disableDefaultUI = true;
            } else {
                mapOptions.mapTypeControl = false;
                mapOptions.fullscreenControl = false;
            }

            this._googleMap = new this._gmaps.Map(this._map, mapOptions);
            this._spotMarker = new this._gmaps.Marker({
                map: this._googleMap,
                position: this._spotLatLng,
                icon: {
                    url: `${this._iconBasePath}/parking-pin.png`,
                    scaledSize: new this._gmaps.Size(
                        this._iconSize,
                        this._iconSize
                    ),
                },
            });
            this._airportMarker = new this._gmaps.Marker({
                map: this._googleMap,
                position: this._destinationLatLng,
                icon: {
                    url: `${this._iconBasePath}/map-pin.png`,
                    scaledSize: new this._gmaps.Size(
                        this._iconSize,
                        this._iconSize
                    ),
                },
            });
            this._directionsDisplay.setMap(this._googleMap);

            if (onClick) {
                this._googleMap.addListener('click', onClick);
            }

            window.addEventListener(
                'resize',
                debounce(this._onWindowResize, 100)
            );

            this._onWindowResize();
            this._getDirections();
        }, 100);
    }

    componentWillUnmount() {
        const {onClick} = this.props;

        if (onClick) {
            this._gmaps.event.clearListeners(this._googleMap, 'click');
        }

        window.removeEventListener('resize', this._onWindowResize);
    }

    _onWindowResize = () => {
        this._googleMap.fitBounds(this._bounds);
    };

    _getDirections() {
        this._directionsService.route(
            {
                origin: this._spotLatLng,
                destination: this._destinationLatLng,
                travelMode: this.props.isDriving ? 'DRIVING' : 'WALKING',
            },
            (result, status) => {
                if (status === 'OK') {
                    this._directionsDisplay.setDirections(result);
                    this._onWindowResize();
                }
            }
        );
    }

    render() {
        const {
            distance,
            distanceInMiles,
            walkingDuration,
            showDuration,
            isDriving,
        } = this.props;

        return (
            <div className="SpotDetailsDirectionsMap">
                <div
                    ref={node => {
                        this._map = node;
                    }}
                    className="SpotDetailsDirectionsMap-container"
                />
                {!isDriving && (
                    <WalkingDistanceDisplay
                        distance={distance}
                        distanceInMiles={distanceInMiles}
                        walkingDuration={walkingDuration}
                        showDuration={showDuration}
                    />
                )}
            </div>
        );
    }
}
